import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { LoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { MiningAccount, loadBalancerAPI } from '../../../api/load-balancer';
import cryptoRandomString from 'crypto-random-string';
import { useRecoilState } from 'recoil';
import { selectedMiningAccountState } from '../../../api';
import { useTranslation } from 'react-i18next';

const generateRandomPassword = () =>
  cryptoRandomString({
    length: 10,
    type: 'alphanumeric',
  });

export const ResetPasswordModal = ({
  miningAccount,
  onSuccess,
  onError,
  onClose,
}: {
  miningAccount: MiningAccount;
  onSuccess: (message: string, refetchKey: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
}) => {
  const [selectedMiningAccount, setSelectedMiningAccount] = useRecoilState(selectedMiningAccountState);
  const { mutate: resetMiningAccountPassword, isLoading: isResetMiningAccountPasswordLoading } =
    loadBalancerAPI.hookWrapperMutation.useResetMiningAccountPassword();
  const { t } = useTranslation();

  const formik = useFormik({
    initialValues: {
      password: generateRandomPassword(),
    },
    onSubmit: (values) => {
      if (isApiLoading) return;

      resetMiningAccountPassword(
        { id: miningAccount.id, username: miningAccount.username, newPassword: values.password },
        {
          onSuccess: () => {
            if (selectedMiningAccount && selectedMiningAccount.id === miningAccount.id)
              setSelectedMiningAccount((prevState) => ({ ...prevState, password: values.password }));
            onSuccess(t('resetPasswordModal.passwordChangeSuccessfully'), values.password);
            onClose();
          },
          onError: (error) => {
            onError(error.message);
          },
        }
      );
    },
  });

  const isApiLoading = isResetMiningAccountPasswordLoading;

  const generatePassword = () => {
    formik.setFieldValue('password', generateRandomPassword());
  };

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: 'form',
        onSubmit: formik.handleSubmit,
        style: {
          maxWidth: '500px',
          width: '100%',
        },
      }}
    >
      <DialogTitle>{t('resetPasswordModal.resetMiningAccountPassword')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ py: 4 }}>
        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
          <TextField
            fullWidth
            id="password"
            name="password"
            label={t('resetPasswordModal.password')}
            value={formik.values.password}
            onChange={() => {}}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            size="small"
          />
          <Button
            variant="outlined"
            onClick={generatePassword}
            sx={{ px: 2, color: 'appBlue.contrastText' }}
            size="small"
          >
            {t('resetPasswordModal.generate')}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="info" size="small">
          {t('resetPasswordModal.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isApiLoading || Object.keys(formik.errors).length > 0}
          variant="contained"
          size="small"
        >
          {t('resetPasswordModal.submit')}
        </Button>
      </DialogActions>
      {isApiLoading && <LoadingOverlay />}
    </Dialog>
  );
};
